import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { StatusPill } from '@/components/px/StatusPill'
import { adminNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { QuestionnaireDrawer } from '@/features/modules/QuestionnaireDrawer'
import {
  fetchDefinitions,
  fetchModuleProjects,
  fetchProjectModules,
  moduleErrorDismissed,
  modulesRetryClicked,
  selectJoinedModuleRows,
  selectModuleMutationError,
  selectModuleProjects,
  selectPendingModuleId,
  toggleModuleActivation,
  type JoinedModuleRow
} from '@/features/modules/moduleCatalogueSlice'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { useAppDispatch, useAppSelector } from '@/store'

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/**
 * The shared project picker (plan Decision 12): this feature's OWN
 * `{id, name}` projection, never a shared `features/projects` file.
 */
function ProjectPicker({
  projects,
  value,
  onSelectionChanged
}: {
  projects: Array<{ id: string; name: string }>
  value: string
  onSelectionChanged: (id: string) => void
}) {
  const { t } = useTranslation('modules')
  return (
    <label className="flex flex-col gap-1 text-sm text-ink-soft">
      {t('picker.label')}
      <select
        value={value}
        onChange={(event) => onSelectionChanged(event.target.value)}
        className={`${inputClass} w-64`}
      >
        <option value="">{t('picker.placeholder')}</option>
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
    </label>
  )
}

/** One joined catalogue row: definition facts + the project's activation. */
function ModuleRow({
  row,
  pending,
  onToggleClicked,
  onConfigureClicked
}: {
  row: JoinedModuleRow
  pending: boolean
  onToggleClicked: (enabled: boolean) => void
  onConfigureClicked: () => void
}) {
  const { t } = useTranslation('modules')
  const { definition, module } = row
  const enabled = module?.enabled === true
  const hasQuestionnaire = definition.questionnaire !== null || module?.hasQuestionnaire === true

  return (
    <TableRow>
      <TableHead
        scope="row"
        className="h-auto max-w-[18rem] whitespace-normal py-2.5 pl-3 align-top font-normal"
      >
        <span className="block text-sm font-medium text-foreground">{definition.name}</span>
        <span className="mt-0.5 block font-mono text-xs text-muted-foreground">
          {definition.moduleId}
        </span>
      </TableHead>

      <TableCell className="max-w-[22rem] whitespace-normal py-2.5 align-top text-sm text-ink-soft">
        {definition.description}
      </TableCell>

      <TableCell className="py-2.5 align-top">
        <span className="flex flex-wrap gap-1">
          <Badge variant="outline" className="font-normal">
            {definition.type}
          </Badge>
          {definition.tags.map((tag) => (
            <Badge key={tag} variant="outline" className="font-normal text-muted-foreground">
              {tag}
            </Badge>
          ))}
        </span>
      </TableCell>

      <TableCell className="py-2.5 align-top">
        <span className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={enabled}
            disabled={pending || module === null}
            title={t(enabled ? 'table.deactivate' : 'table.activate')}
            onClick={() => onToggleClicked(!enabled)}
            className={cnSwitch(enabled)}
          >
            <span
              aria-hidden
              className={`pointer-events-none block h-4 w-4 translate-x-0.5 rounded-full bg-card shadow transition-transform ${
                enabled ? 'translate-x-[1.125rem]' : ''
              }`}
            />
          </button>
          <span className="sr-only">
            {enabled ? t('table.activeSr') : t('table.inactiveSr')}
          </span>
          {pending ? (
            <span className="text-xs text-muted-foreground">{t('table.pending')}</span>
          ) : (
            <StatusPill tone={enabled ? 'done' : 'neutral'}>
              {enabled ? t('table.active') : t('table.inactive')}
            </StatusPill>
          )}
        </span>
      </TableCell>

      <TableCell className="py-2.5 pr-3 text-right align-top">
        {hasQuestionnaire ? (
          <button
            type="button"
            onClick={onConfigureClicked}
            disabled={module === null}
            className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted disabled:opacity-60"
          >
            {t('table.configure')}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">{t('table.noQuestionnaire')}</span>
        )}
      </TableCell>
    </TableRow>
  )
}

/** The switch chrome, kept beside the row so tones stay in one place. */
const cnSwitch = (enabled: boolean): string =>
  `flex h-6 w-11 items-center rounded-full border transition-colors ${
    enabled ? 'border-primary bg-primary' : 'border-input bg-muted'
  }`

/**
 * `/admin/modules` (PX-W6.5, AC-1 + AC-2) — the module catalogue joined with
 * the selected project's activation rows. The catalogue is read-only
 * reference data (INV-5); the only writes are the per-project PATCH
 * attachment routes, fired from the toggle and the questionnaire drawer.
 */
export default function ModulesPage() {
  const { t } = useTranslation(['modules', 'common'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const projects = useAppSelector(selectModuleProjects)
  const projectsCache = useAppSelector((s) => s.modules.projectsCache)
  const definitionsCache = useAppSelector((s) => s.modules.definitionsCache)
  const rowsCache = useAppSelector((s) => s.modules.projectCache)
  const rowsError = useAppSelector((s) => s.modules.error)
  const mutationError = useAppSelector(selectModuleMutationError)
  const rows = useAppSelector(selectJoinedModuleRows)
  const pendingModuleId = useAppSelector(selectPendingModuleId)

  const [projectId, setProjectId] = useState('')
  const [configureKey, setConfigureKey] = useState<string | null>(null)

  useEffect(() => {
    void dispatch(fetchDefinitions())
    void dispatch(fetchModuleProjects())
  }, [dispatch])

  useEffect(() => {
    if (projectId !== '') void dispatch(fetchProjectModules(projectId))
  }, [dispatch, projectId])

  const loading = definitionsCache.loading || projectsCache.loading
  const drawerRow = rows.find((row) => row.definition.moduleId === configureKey) ?? null

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={<span>{t('chrome.breadcrumb')}</span>}
      contextPill={t('chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('lede')}</p>
      </header>

      <section aria-label={t('picker.label')} className="mt-4">
        <ProjectPicker projects={projects} value={projectId} onSelectionChanged={setProjectId} />
      </section>

      {mutationError !== null && (
        <p role="alert" className="mt-3 text-sm text-blocked">
          {toMessage(mutationError)}
        </p>
      )}

      {projectId === '' ? (
        <div className="mt-4">
          <EmptyPanel title={t('empty.noProjectTitle')} body={t('empty.noProjectBody')} />
        </div>
      ) : loading ? (
        <div className="mt-4">
          <LoadingPanel />
        </div>
      ) : rowsError !== null && rowsError.status === 403 ? (
        <div className="mt-4">
          <ForbiddenPanel requested="/{orgId}/projects/:projectId/modules" />
        </div>
      ) : rowsError !== null ? (
        <div className="mt-4">
          <ErrorPanel
            error={rowsError}
            onRetryClicked={() => {
              dispatch(modulesRetryClicked())
              void dispatch(fetchDefinitions())
              void dispatch(fetchModuleProjects())
              if (projectId !== '') void dispatch(fetchProjectModules(projectId))
            }}
          />
        </div>
      ) : rowsCache.loading ? (
        <div className="mt-4">
          <LoadingPanel />
        </div>
      ) : rows.length === 0 ? (
        <div className="mt-4">
          <EmptyPanel body={t('empty.catalogue')} />
        </div>
      ) : (
        <section className="mt-4 rounded-lg border border-border bg-card" aria-label={t('title')}>
          <Table>
            <TableCaption className="sr-only">{t('table.caption')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={HEAD_CLASS + ' pl-3'}>
                  {t('table.columns.module')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.description')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.level')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('table.columns.activation')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS + ' pr-3 text-right'}>
                  {t('table.columns.questionnaire')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <ModuleRow
                  key={row.definition.moduleId}
                  row={row}
                  pending={pendingModuleId === row.definition.moduleId}
                  onToggleClicked={(enabled) =>
                    void dispatch(
                      toggleModuleActivation({
                        projectId,
                        moduleId: row.definition.moduleId,
                        enabled
                      })
                    )
                  }
                  onConfigureClicked={() => setConfigureKey(row.definition.moduleId)}
                />
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      {drawerRow !== null && drawerRow.module !== null && (
        <div className="mt-4" data-testid="questionnaire-drawer">
          <QuestionnaireDrawer
            definition={drawerRow.definition}
            module={drawerRow.module}
            projectId={projectId}
            onClose={() => {
              dispatch(moduleErrorDismissed())
              setConfigureKey(null)
            }}
          />
        </div>
      )}

      <p className="mt-6 border-t border-border pt-3 text-xs italic text-muted-foreground">
        {t('densityNote')}
      </p>
    </AppShell>
  )
}
